import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Footprints,
  Frown,
  Meh,
  Smile,
  Zap,
  Flame,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Workout, useWorkouts } from "@/hooks/useWorkouts";
import { findGpsCardioExercise } from "@/lib/gpsCardioWorkout";
import { PlatformMap } from "@/components/PlatformMap";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import { ShareIcon } from "@/components/icons/ShareIcon";
import { useKeyboardHeight } from "@/hooks/useKeyboardHeight";
import { toast } from "sonner";

const feelingOptions = [
  { id: "exhausted", icon: Frown, label: "Exhausted", color: "text-red-400" },
  { id: "tough", icon: Meh, label: "Tough", color: "text-orange-400" },
  { id: "good", icon: Smile, label: "Good", color: "text-yellow-400" },
  { id: "strong", icon: Zap, label: "Strong", color: "text-primary" },
  { id: "amazing", icon: Flame, label: "Amazing", color: "text-primary" },
] as const;

interface RunHistoryDetailModalProps {
  workout: Workout | null;
  open: boolean;
  onClose: () => void;
}

export const RunHistoryDetailModal = ({
  workout,
  open,
  onClose,
}: RunHistoryDetailModalProps) => {
  const { t } = useTranslation();
  const { updateWorkout, deleteWorkout } = useWorkouts();
  const [title, setTitle] = useState("");
  const [feeling, setFeeling] = useState("");
  const [notes, setNotes] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const keyboardHeight = useKeyboardHeight();

  const scrollInputIntoView = useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target;
    const delay = Capacitor.getPlatform() === "ios" ? 450 : 350;
    setTimeout(() => {
      if (document.activeElement === target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, delay);
  }, []);

  // Parse GPS cardio (run / walk / hike) data from exercise notes
  const runningExercise = workout ? findGpsCardioExercise(workout.exercises) : undefined;
  let runningData: any = null;
  if (runningExercise?.notes) {
    try {
      runningData = JSON.parse(runningExercise.notes);
    } catch {}
  }

  const distanceKm = runningExercise?.distance_km || 0;
  const durationSeconds = runningExercise?.duration_seconds || 0;
  const pace = runningData?.pace || "--:--";
  const splits: Array<{ km: number; time: string; pace: string }> =
    runningData?.splits || [];
  const routePoints: Array<{ lat: number; lng: number }> =
    runningData?.routePoints || [];
  const caloriesBurned = workout?.calories_burned || 0;

  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  // Initialize editable fields when workout changes
  useEffect(() => {
    if (workout) {
      setTitle(workout.title || "");
      // Extract feeling from difficulty_level (stored as feeling label) or notes
      const savedFeeling = feelingOptions.find(
        (f) => f.label === workout.difficulty_level
      );
      setFeeling(savedFeeling?.id || runningData?.feeling || "");
      // Extract user notes (strip auto-generated feeling/splits lines)
      const rawNotes = workout.notes || "";
      const userNotes = rawNotes
        .split("\n")
        .filter(
          (line) =>
            !line.startsWith("Feeling:") && !line.startsWith("Splits:")
        )
        .join("\n")
        .trim();
      setNotes(userNotes);
    }
  }, [workout, runningData?.feeling]);

  const handleSave = async () => {
    if (!workout?.id) return;
    setSaving(true);
    try {
      const feelingLabel =
        feelingOptions.find((f) => f.id === feeling)?.label || "";
      const workoutNotes = [
        notes.trim(),
        feelingLabel ? `Feeling: ${feelingLabel}` : "",
        splits.length > 0
          ? `Splits: ${splits.map((s) => `${s.km}km: ${s.pace}/km`).join(", ")}`
          : "",
      ]
        .filter(Boolean)
        .join("\n");

      await updateWorkout(workout.id, {
        title: title.trim() || workout.title,
        difficulty_level: feelingLabel || workout.difficulty_level,
        notes: workoutNotes,
      });
      toast.success(t('components.runHistoryDetail.toast.runUpdated'));
      onClose();
    } catch {
      toast.error(t('components.runHistoryDetail.toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!workout?.id) return;
    await deleteWorkout(workout.id);
    setShowDeleteConfirm(false);
    toast.success(t('components.runHistoryDetail.toast.runDeleted'));
    onClose();
  };

  if (!workout) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          className="sm:max-w-md max-w-[calc(100vw-2rem)] bg-background border-2 border-primary/30 shadow-2xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto [&>button]:hidden"
          style={{
            paddingBottom: keyboardHeight > 0 ? `calc(1.5rem + ${keyboardHeight}px)` : undefined,
          }}
        >
          <DialogHeader className="text-center space-y-2 pb-2">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-10 h-10 flex items-center justify-center text-destructive hover:text-destructive/80"
              >
                <Trash2 className="h-5 w-5" />
              </button>
              <div className="mx-auto w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                <Footprints className="h-6 w-6 text-primary-foreground" />
              </div>
              <button
                onClick={() => setShowShareModal(true)}
                className="w-10 h-10 flex items-center justify-center text-primary hover:text-primary/80"
              >
                <ShareIcon className="h-5 w-5" />
              </button>
            </div>
            <DialogTitle className="text-lg font-bold text-center text-foreground">
              {t('components.runHistoryDetail.runDetails')}
            </DialogTitle>
            {workout.completed_at && (
              <p className="text-xs text-muted-foreground">
                {new Date(workout.completed_at).toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Route Map */}
            {routePoints.length > 1 && (
              <div className="rounded-xl overflow-hidden border border-border/30 h-48">
                <PlatformMap
                  center={
                    [routePoints[Math.floor(routePoints.length / 2)].lat, routePoints[Math.floor(routePoints.length / 2)].lng] as [number, number]
                  }
                  routePoints={routePoints.map(p => [p.lat, p.lng] as [number, number])}
                  showUserMarker={false}
                  showRoute={true}
                  showStartFinishMarkers={true}
                  showKmMarkers={true}
                  fitToRoute={true}
                  autoFollow={false}
                  className="w-full h-full"
                  darkMode={true}
                  interactive={true}
                  zoom={14}
                />
              </div>
            )}

            {/* Editable Title */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
                {t('components.runHistoryDetail.workoutName')} <Pencil className="h-3 w-3" />
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onFocus={scrollInputIntoView}
                className="w-full text-center font-semibold px-3 py-2 rounded-lg border border-border bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder={t('components.runHistoryDetail.runTitlePlaceholder')}
              />
            </div>

            {/* Stats Grid (read-only) */}
            <div className="grid grid-cols-2 gap-3 text-sm bg-muted/30 rounded-xl p-4 border border-border/30">
              <div className="text-center">
                <div className="font-bold text-lg text-primary">
                  {distanceKm} km
                </div>
                <div className="text-xs text-muted-foreground">{t('components.runHistoryDetail.distance')}</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg">
                  {formatTime(durationSeconds)}
                </div>
                <div className="text-xs text-muted-foreground">{t('components.runHistoryDetail.totalTime')}</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg">{pace}</div>
                <div className="text-xs text-muted-foreground">
                  {t('components.runHistoryDetail.avgPacePerKm')}
                </div>
              </div>
              <div className="text-center">
                <div className="font-bold text-lg">
                  {caloriesBurned || "--"}
                </div>
                <div className="text-xs text-muted-foreground">{t('components.runHistoryDetail.calories')}</div>
              </div>
            </div>

            {/* Splits (read-only) */}
            {splits.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">
                  {t('components.runHistoryDetail.splitsLabel')}
                </label>
                <div className="space-y-1 max-h-32 overflow-y-auto bg-muted/20 rounded-lg p-2">
                  {splits.map((split, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center text-sm px-2 py-1.5 rounded-md hover:bg-muted/20"
                    >
                      <span className="text-muted-foreground font-medium">
                        {split.km} km
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {split.time}
                        </span>
                        <span className="font-mono font-semibold text-primary">
                          {split.pace}/km
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Feeling (editable) */}
            <div>
              <label className="text-xs text-muted-foreground mb-2 block">
                {t('components.runHistoryDetail.feelingPrompt')}
              </label>
              <div className="flex justify-between gap-2">
                {feelingOptions.map((option) => {
                  const IconComponent = option.icon;
                  const localizedLabel = t(`components.runHistoryDetail.feeling.${option.id}`);
                  return (
                    <button
                      key={option.id}
                      onClick={() => setFeeling(option.id)}
                      className={`flex-1 py-3 rounded-lg transition-all flex items-center justify-center ${
                        feeling === option.id
                          ? "bg-primary/20 ring-2 ring-primary scale-110"
                          : "bg-muted/30 hover:bg-muted/50"
                      }`}
                      title={localizedLabel}
                    >
                      <IconComponent
                        className={`h-6 w-6 ${feeling === option.id ? option.color : "text-muted-foreground"}`}
                      />
                    </button>
                  );
                })}
              </div>
              {feeling && (
                <p className="text-xs text-center text-muted-foreground mt-1">
                  {t(`components.runHistoryDetail.feeling.${feeling}`)}
                </p>
              )}
            </div>

            {/* Notes (editable) */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">
                {t('components.runHistoryDetail.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onFocus={scrollInputIntoView}
                className="w-full rounded-lg border border-border bg-background/50 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                rows={2}
                placeholder={t('components.runHistoryDetail.notesPlaceholder')}
                style={{ scrollMarginBottom: keyboardHeight > 0 ? keyboardHeight + 80 : 100 }}
              />
            </div>

            {/* Save / Close */}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1 bg-gradient-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t('components.runHistoryDetail.saving') : t('components.runHistoryDetail.saveChanges')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
      >
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('components.runHistoryDetail.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('components.runHistoryDetail.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('components.runHistoryDetail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Modal */}
      {showShareModal && workout && (
        <ShareWorkoutModal
          workout={{
            ...workout,
            distance_km: distanceKm,
            average_pace: pace,
            route_coordinates: routePoints,
            duration_seconds: durationSeconds,
            pace_seconds_per_km: distanceKm > 0 ? durationSeconds / distanceKm : 0,
            calories_burned: workout.calories_burned ?? caloriesBurned,
            splits: splits,
          } as any}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </>
  );
};
